import Button from '../../../libs/button/button.js';
import { div } from '../../../scripts/dom-builder.js';
import checkIsFromSAP from '../../../scripts/utils/sap-utils.js';

const FILE_NAME = 'SAP ECCN Matrix.xlsx';
const EXCEL_TABLE_STYLES = {
  height: 35,
  size: 12,
  cellColor: 'DDEBF7',
  linkCellTextColor: '0000FF',
  cellIndent: 5,
};

export function getHeaders(data) {
  return data.length ? Object.keys(data[0]) : [];
}

function stripHtml(html) {
  const tmp = div();
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

export function prepareDataBeforeDownload(data) {
  const headers = getHeaders(data);
  const LINK_HEADER = headers[headers.length - 1];
  const NUM_COLUMNS_TO_CLEAN = 3;
  const COLUMNS_TO_CLEAN = headers.slice(0, NUM_COLUMNS_TO_CLEAN);

  return data.map((item) => {
    const updatedItem = { ...item };

    COLUMNS_TO_CLEAN.forEach((col) => {
      if (typeof updatedItem[col] === 'string') {
        updatedItem[col] = stripHtml(updatedItem[col]);
      }
    });

    if (item[LINK_HEADER] && item[headers[0]] && checkIsFromSAP()) {
      updatedItem[headers[0]] = {
        name: updatedItem[headers[0]],
        url: item[LINK_HEADER],
      };
    }

    delete updatedItem[LINK_HEADER];

    return updatedItem;
  });
}

async function dataToExcel(jsonData) {
  const headers = getHeaders(jsonData);
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet 1');
  const PRODUCT_VERSION_HEADER = headers[0];
  const headerRow = worksheet.addRow(headers);

  headerRow.eachCell((cell) => {
    cell.value = cell.value && cell.value.replace(/\n/g, ' ');
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_TABLE_STYLES.cellColor },
    };
    cell.font = { bold: true, size: EXCEL_TABLE_STYLES.size };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });

  worksheet.getRow(1).height = EXCEL_TABLE_STYLES.height;

  jsonData.forEach((obj) => {
    const row = worksheet.addRow(headers.map((header) => obj[header]));
    const firstcolumnCell = row.getCell(1); // +1 because getCell is 1-based
    const firstcolumnValue = obj[PRODUCT_VERSION_HEADER];

    if (
      typeof firstcolumnValue === 'object' &&
      firstcolumnValue.name &&
      firstcolumnValue.url
    ) {
      firstcolumnCell.value = {
        text: firstcolumnValue.name,
        hyperlink: firstcolumnValue.url,
      };
      firstcolumnCell.font = {
        color: { argb: EXCEL_TABLE_STYLES.linkCellTextColor },
        underline: true,
      };
    }
  });

  worksheet.columns = headers.map((header) => ({
    header,
    key: header,
    width:
      Math.max(
        ...jsonData.map((row) => String(row[header]).length),
        header.length,
      ) + EXCEL_TABLE_STYLES.cellIndent,
  }));

  const blob = await workbook.xlsx.writeBuffer();
  const blobData = new Blob([blob], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');

  link.href = URL.createObjectURL(blobData);
  link.download = FILE_NAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function makeDownloadButton(placeholders) {
  const downloadLabel =
    placeholders.finderVariationEccnResultsDownloadExcelLinkText ||
    'Download the table';
  const downloadButton = new Button(
    downloadLabel,
    'icon-download',
    'tertiary',
    'large',
    null,
    false,
    'icon-start',
    downloadLabel,
  ).render();
  const buttonElement = downloadButton.firstChild;

  buttonElement.setAttribute('tabindex', 0);
  buttonElement.setAttribute('disabled', true);

  return downloadButton;
}

export function downloadExcel(data) {
  dataToExcel(prepareDataBeforeDownload(data));
}
